import React, { useState, useMemo, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
// import { useSupabaseAdmin } from '../../contexts/SupabaseAdminContext'; // Provider removed
import { Category } from '../../types';
import { runFullMigration, runLegacyMigration } from '../../lib/migration';

const AdminCategories: React.FC = () => {
  const { 
    categories,
    flatCategories,
    addCategory,
    updateCategory, 
    deleteCategory,
    isLoading: loading
  } = useAdmin();

  // Direct usage from AdminContext (Single Source of Truth)
  // categories, flatCategories, CRUD functions all provided by AdminContext

  // Debug: real-time logging
  console.log('🔥 AdminCategories LIVE DEBUG:', {
    categoriesLength: categories?.length || 0,
    firstCategory: categories?.[0]?.name || 'None',
    isLoading: loading,
    timestamp: new Date().toLocaleTimeString()
  });
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedParent, setSelectedParent] = useState<number | undefined>(undefined);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [migrating, setMigrating] = useState(false);

  // Debug: detect categories change
  useEffect(() => {
    console.log('Categories changed:', {
      length: categories?.length || 0,
      isLoading: loading,
      categories: categories?.slice(0, 2)?.map(c => ({ id: c.id, name: c.name })) || []
    });
  }, [categories, loading]);

  const getDescendantIds = (categoryId: number): number[] => {
    const descendants = new Set<number>();
    const findChildren = (id: number) => {
      flatCategories
        .filter(c => c.parentId === id)
        .forEach(child => {
          descendants.add(child.id);
          findChildren(child.id);
        });
    };
    findChildren(categoryId);
    return Array.from(descendants);
  };

  const handleAddCategory = async () => {
    if (newCategoryName.trim()) {
      try {
        await addCategory(newCategoryName.trim(), selectedParent);
        // Reset state on success
        setNewCategoryName('');
        setSelectedParent(undefined);
        setLocalError(null);
      } catch (err) {
        setLocalError('An error occurred while adding the category.');
      }
    }
  };

  const handleEditCategory = (category: Category) => {
    console.log('🔍 Editing category:', { id: category.id, name: category.name, parentId: category.parentId });
    setEditingCategory(category);
    setNewCategoryName(category.name);
    setSelectedParent(category.parentId);
    setLocalError(null);
  };

  const handleUpdateCategory = () => {
    if (!editingCategory || !newCategoryName.trim()) return;

    // Circular dependency check
    if (selectedParent) {
      if (selectedParent === editingCategory.id) {
        setLocalError('A category cannot be its own parent.');
        return;
      }
      const descendantIds = getDescendantIds(editingCategory.id);
      if (descendantIds.includes(selectedParent)) {
        setLocalError('A category cannot be moved under its own subcategory.');
        return;
      }
    }

    updateCategory(editingCategory.id, {
      name: newCategoryName.trim(),
      parentId: selectedParent,
    });
    
    cancelEdit();
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setNewCategoryName('');
    setSelectedParent(undefined);
    setLocalError(null);
  };

  const handleDeleteCategory = async (id: number) => {
    const categoryToDelete = flatCategories.find(c => c.id === id);
    const children = flatCategories.filter(c => c.parentId === id);
    
    let confirmationMessage = 'Would you like to delete this category?';
    if (children.length > 0) {
      confirmationMessage += `\n\nWarning: This category has ${children.length} subcategory(s). Deleting the parent will also delete all subcategories.`
    }

    if (window.confirm(confirmationMessage)) {
      try {
        await deleteCategory(id);
        // Reset error state on success
        setLocalError(null);
      } catch (err) {
        setLocalError('An error occurred while deleting the category.');
      }
    }
  };

  const handleMigration = async () => {
    if (!window.confirm('Would you like to migrate to the new category structure based on product_category.txt? Existing Supabase category data will be deleted.')) {
      return;
    }

    setMigrating(true);
    setLocalError(null);

    try {
      const result = await runFullMigration();
      console.log('Migration result:', result);
      
      if (result.categories.success && result.products.success) {
        alert('New category structure migration completed successfully!');
        // State is automatically updated, no refresh needed
      } else {
        setLocalError(`Error during migration: ${result.categories.message || result.products.message}`);
      }
    } catch (err) {
      console.error('Migration error:', err);
      setLocalError('An error occurred during migration.');
    } finally {
      setMigrating(false);
    }
  };

  const handleLegacyMigration = async () => {
    if (!window.confirm('Would you like to migrate localStorage category data to Supabase? Existing Supabase category data will be deleted.')) {
      return;
    }

    setMigrating(true);
    setLocalError(null);

    try {
      const result = await runLegacyMigration();
      console.log('Legacy migration result:', result);
      
      if (result.categories.success && result.products.success) {
        alert('localStorage migration completed successfully!');
        // State is automatically updated, no refresh needed
      } else {
        setLocalError(`Error during migration: ${result.categories.message || result.products.message}`);
      }
    } catch (err) {
      console.error('Legacy migration error:', err);
      setLocalError('An error occurred during migration.');
    } finally {
      setMigrating(false);
    }
  };

  const availableParents = useMemo(() => {
    if (!editingCategory) return flatCategories;
    const descendantIds = getDescendantIds(editingCategory.id);
    const idsToDisable = new Set([editingCategory.id, ...descendantIds]);
    return flatCategories.filter(cat => !idsToDisable.has(cat.id));
  }, [editingCategory, flatCategories]);

  const renderCategoryTree = (cats: Category[], level = 0) => {
    return cats.map((category) => (
      <React.Fragment key={category.id}>
        <tr className={`bg-white hover:bg-neutral-50 transition-colors duration-150 ease-in-out ${level > 0 ? 'border-l-2 border-forest-200' : ''}`}>
          <td className="px-4 py-3 border-b border-neutral-200 text-sm">{category.id}</td>
          <td className="px-4 py-3 border-b border-neutral-200 text-sm">
            <div style={{ paddingLeft: `${level * 24}px` }} className="flex items-center">
              {level > 0 && <span className="text-neutral-400 mr-2">└─</span>}
              <span className={level === 0 ? 'font-bold text-neutral-800' : 'text-neutral-600'}>
                {category.name}
              </span>
            </div>
          </td>
          <td className="px-4 py-3 border-b border-neutral-200 text-sm">
            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${level === 0 ? 'bg-forest-100 text-forest-800' : 'bg-green-100 text-green-800'}`}>
              Level {level}
            </span>
          </td>
          <td className="px-4 py-3 border-b border-neutral-200 text-sm">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleEditCategory(category)}
                className="text-indigo-600 hover:text-indigo-900 transition-colors duration-150"
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteCategory(category.id)}
                className="text-red-600 hover:text-red-900 transition-colors duration-150"
              >
                Delete
              </button>
            </div>
          </td>
        </tr>
        {category.children && category.children.length > 0 && renderCategoryTree(category.children, level + 1)}
      </React.Fragment>
    ));
  };

  return (
    <div className="container mx-auto p-6 bg-neutral-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-neutral-800">Hierarchical Category Management</h1>
        <div className="flex gap-2">
          <button
            onClick={handleMigration}
            disabled={migrating}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-neutral-400"
          >
            {migrating ? 'Migrating...' : 'New Category Structure Migration'}
          </button>
          <button
            onClick={handleLegacyMigration}
            disabled={migrating}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-neutral-400"
          >
            localStorage Migration
          </button>
          <div className="inline-flex items-center px-4 py-2 text-sm text-neutral-600 bg-neutral-100 rounded-md">
            Syncing in real-time...
          </div>
        </div>
      </div>
      
      <div className="mb-8 p-6 border rounded-xl bg-white shadow-sm">
        <h2 className="text-xl font-semibold text-neutral-700 mb-4">
          {editingCategory ? `Edit Category: ${editingCategory.name}` : 'Add New Category'}
        </h2>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="category-name" className="block text-sm font-medium text-neutral-700 mb-1">
              Category Name
            </label>
            <input
              id="category-name"
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="mt-1 block w-full max-w-lg rounded-md border-neutral-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Enter category name"
            />
          </div>
          
          <div>
            <label htmlFor="parent-category" className="block text-sm font-medium text-neutral-700 mb-1">
              Parent Category (optional)
            </label>
            <select
              id="parent-category"
              value={selectedParent || ''}
              onChange={(e) => setSelectedParent(e.target.value ? parseInt(e.target.value) : undefined)}
              className="mt-1 block w-full max-w-lg rounded-md border-neutral-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">Top-level Category</option>
              {availableParents.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {'  '.repeat(cat.level)}└ {cat.name}
                  </option>
                ))
              }
            </select>
          </div>

          {localError && <p className="text-sm text-red-600">{localError}</p>}
          
          <div className="flex items-center space-x-3 pt-2">
            {editingCategory ? (
              <>
                <button onClick={handleUpdateCategory} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                  Save Changes
                </button>
                <button onClick={cancelEdit} className="inline-flex justify-center py-2 px-4 border border-neutral-300 shadow-sm text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                  Cancel
                </button>
              </>
            ) : (
              <button onClick={handleAddCategory} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-forest-400 hover:bg-forest-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500">
                Add Category
              </button>
            )}
          </div>
        </div>

        {/* Loading and error display */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-forest-400 text-sm">Loading categories...</div>
          </div>
        )}
        {localError && (
          <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
            {localError}
          </div>
        )}
      </div>

      {/* Category table - shown only after loading completes */}
      {!loading && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-xl font-semibold text-neutral-700">Category List</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">ID</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Category Name</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Level</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {categories.length > 0 
                ? renderCategoryTree(categories)
                : (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-sm text-neutral-500">
                      No categories found. Try adding a new category.
                    </td>
                  </tr>
                )
              }
            </tbody>
          </table>
        </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
